
import * as XLSX from 'xlsx';
import { formatDateTime } from './date-utils';

/**
 * Prepares data for Excel export by cleaning and formatting
 */
const prepareDataForExport = (data: any) => {
  // Function to clean object for export (remove circular references, etc.)
  const cleanObject = (obj: any) => {
    if (!obj) return {};
    
    // Create a new object to store cleaned data
    const cleaned: any = {};
    
    Object.keys(obj).forEach(key => {
      // Skip functions or complex objects that can't be serialized
      if (typeof obj[key] === 'function') return;
      
      // For nested objects/arrays that aren't null/undefined
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        // Handle Date objects
        if (obj[key] instanceof Date) {
          cleaned[key] = formatDateTime(obj[key].toISOString());
        }
        // Handle arrays
        else if (Array.isArray(obj[key])) {
          // For arrays, we'll create sheet per data type later
          cleaned[key] = 'See separate sheet';
        }
        // Handle other nested objects (shallow cleaning only)
        else {
          const nestedObj: any = {};
          Object.keys(obj[key]).forEach(nestedKey => {
            if (typeof obj[key][nestedKey] !== 'function' && typeof obj[key][nestedKey] !== 'object') {
              nestedObj[nestedKey] = obj[key][nestedKey];
            }
          });
          cleaned[key] = JSON.stringify(nestedObj);
        }
      } 
      // For ISO date strings, format them nicely
      else if (typeof obj[key] === 'string' && 
               obj[key].match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        cleaned[key] = formatDateTime(obj[key]);
      }
      // For primitive values
      else {
        cleaned[key] = obj[key];
      }
    });
    
    return cleaned;
  };

  // Process each section of data
  const result: any = {};
  
  // Handle array data by mapping each item to a cleaned version
  Object.keys(data).forEach(key => {
    if (Array.isArray(data[key])) {
      result[key] = data[key].map((item: any) => cleanObject(item));
    } else if (typeof data[key] === 'object' && data[key] !== null) {
      result[key] = [cleanObject(data[key])];
    } else {
      result[key] = [{ value: data[key] }];
    }
  });
  
  return result;
};

/**
 * Exports data to Excel file
 */
export const exportToExcel = (data: any, filename: string) => {
  try {
    // Prepare the data for export
    const preparedData = prepareDataForExport(data);
    
    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    
    // Add each data category as a separate worksheet
    Object.keys(preparedData).forEach(sheetName => {
      if (preparedData[sheetName] && preparedData[sheetName].length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(preparedData[sheetName]);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      }
    });
    
    // Generate the Excel file and trigger download
    XLSX.writeFile(workbook, `${filename}-${new Date().toISOString().split('T')[0]}.xlsx`);
  } catch (error) {
    console.error('Error exporting data to Excel:', error);
    alert('Failed to export data. Please try again.');
  }
};
